import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { supabase } from '../../lib/supabase';
import type { Operator } from '../../services/operators-api';

interface AddOperatorModalProps {
  open: boolean;
  onClose: () => void;
  onOperatorAdded: (operator: Operator) => void;
}

interface FormData {
  name: string;
  website: string;
}

export default function AddOperatorModal({
  open,
  onClose,
  onOperatorAdded,
}: AddOperatorModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    website: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const handleInputChange =
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFormData(prev => ({ ...prev, [field]: value }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.website.trim()) {
      newErrors.website = 'Website is required';
    } else if (!isValidUrl(formData.website)) {
      newErrors.website = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      // Add protocol if missing
      const urlToTest = url.startsWith('http') ? url : `https://${url}`;
      new URL(urlToTest);
      return true;
    } catch {
      return false;
    }
  };

  const normalizeUrl = (url: string): string => {
    return url.startsWith('http') ? url : `https://${url}`;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from('operators')
        .insert({
          name: formData.name.trim(),
          website: normalizeUrl(formData.website.trim()),
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      toast.success('Operator created successfully!');
      onOperatorAdded(data);
      handleClose();
    } catch (error) {
      console.error('Error creating operator:', error);
      toast.error(
        error instanceof Error
          ? `Failed to create operator: ${error.message}`
          : 'Failed to create operator'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', website: '' });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Add New Operator
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={handleInputChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              required
              autoFocus
            />

            <TextField
              label="Website"
              value={formData.website}
              onChange={handleInputChange('website')}
              error={!!errors.website}
              helperText={
                errors.website || 'e.g., example.com or https://example.com'
              }
              fullWidth
              required
              placeholder="example.com"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Operator'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
